import React, { useCallback, useEffect } from 'react'
import {
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
} from '@mui/material'
import { useLicense } from './hooks'
import LicenseHeader from './components/LicenseHeader'
// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member -- LicenseInfoForm has single root; parser false positive
import LicenseInfoForm from './components/LicenseInfoForm'
import UserManagement from './components/UserManagement'
import DistrictSection from './components/DistrictSection'
import SchoolsSection from './components/SchoolsSection'
import HistorySection from './components/HistorySection'

export default function LicenseControlRoom() {
  const {
    loading,
    actionLoading,
    error,
    setError,
    successMsg,
    setSuccessMsg,
    data,
    lCode,
    form,
    formHasChanges,
    updateFormField,
    resetForm,
    postAction,
    parentOrigin,
  } = useLicense()

  useEffect(() => {
    if (data?.license?.name) {
      try {
        window.parent.document.title = `License: ${data.license.name}`
      } catch {
        document.title = `License: ${data.license.name}`
      }
    }
  }, [data?.license?.name])

  const handleRegenerateAccessCode = useCallback(
    () => postAction('regenerateLicenseAccessCode'),
    [postAction]
  )

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>{error}</Alert>
      </Box>
    )
  }

  if (!data) return null

  const {
    license,
    district,
    schools,
    sortedTeachers,
    sortedAdmins,
    history,
    hasMetrics,
  } = data

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: '100%',
        mx: 'auto',
        minHeight: '100vh',
        backgroundColor: 'grey.50',
      }}
    >
      <Snackbar
        open={!!successMsg || !!error}
        autoHideDuration={5000}
        onClose={() => {
          setSuccessMsg(null)
          setError(null)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setSuccessMsg(null)
            setError(null)
          }}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || successMsg}
        </Alert>
      </Snackbar>

      {/* Loading indicator for actions */}
      {actionLoading && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}

      {/* Header */}
      <LicenseHeader
        lCode={lCode}
        license={license}
        district={district}
        hasMetrics={hasMetrics}
        parentOrigin={parentOrigin}
        actionLoading={actionLoading}
        onRegenerateAccessCode={handleRegenerateAccessCode}
      />

      {/* License Info Form */}
      <LicenseInfoForm
        form={form}
        onFieldChange={updateFormField}
        onUpdate={() =>
          postAction('updateLicense', {
            name: form.name,
            licenseType: form.type,
            contactName: form.contactName,
            contactEmail: form.contactEmail,
            startsAt: form.startsAt,
            endsAt: form.endsAt,
            districtName: form.districtName || undefined,
            schoolName: form.schoolName || undefined,
            seatLimit: form.seatLimit || undefined,
          })
        }
        onResetForm={resetForm}
        formHasChanges={formHasChanges}
        actionLoading={actionLoading}
      />

      {/* User Management: Admins + Teachers */}
      <UserManagement
        admins={sortedAdmins}
        teachers={sortedTeachers}
        lCode={lCode}
        parentOrigin={parentOrigin}
        actionLoading={actionLoading}
        postAction={postAction}
        setError={setError}
      />

      <Typography
        variant='h5'
        sx={{
          fontWeight: 600,
          mb: 2,
          borderLeft: '4px solid',
          borderColor: 'primary.main',
          pl: 2,
          py: 0.5,
        }}
      >
        District & Schools
      </Typography>

      {/* District */}
      <DistrictSection
        district={district}
        parentOrigin={parentOrigin}
        actionLoading={actionLoading}
        postAction={postAction}
      />

      {/* Schools */}
      <SchoolsSection
        schools={schools}
        parentOrigin={parentOrigin}
        actionLoading={actionLoading}
        postAction={postAction}
      />

      <Typography
        variant='h5'
        sx={{
          fontWeight: 600,
          mb: 2,
          borderLeft: '4px solid',
          borderColor: 'primary.main',
          pl: 2,
          py: 0.5,
        }}
      >
        License History
      </Typography>
      {/* History */}
      <HistorySection history={history} />
    </Box>
  )
}
